/**
 * LadderPercentField - Wrapper component for LadderPercentInput that handles percent-specific behavior.
 *
 * ARCHITECTURE:
 * - Provides a wrapper around LadderPercentInput with percent-specific behavior logic
 * - Handles percentage value changes and validation
 * - Integrates with debug components
 *
 * RESPONSIBILITIES:
 * - Wrap LadderPercentInput with additional behavior
 * - Handle percentage value validation and clamping
 * - Provide consistent interface for percent input fields
 */

import React, { useMemo, useCallback } from 'react'
import useBuildScenarioStore, {
  useScenarioDriverValue,
} from '../../../../stores/buildScenarioStore'
import { StyledInputNumber } from './styles/InputNumber.styles'
import { useInputValueManager } from './utils/useInputValueManager'
import { formatPercentage, safeParseNumber } from './utils/formatters'
import {
  calculatePercentageDiff,
  convertToPercentageLimit,
  convertToAbsoluteValue,
  applySmartSnapping,
} from './utils/percentageHelpers'

const LadderPercentField = ({
  scenarioId,
  driverId,
  min,
  max,
  step,
  ...props
}) => {
  const driverValueObject = useScenarioDriverValue(scenarioId, driverId)
  const setScenarioDriverValue = useBuildScenarioStore(
    (state) => state.setScenarioDriverValue,
  )

  // Memoize calculations to ensure proper updates when baseline changes
  const { baseDisplayValue, minPercent, maxPercent } = useMemo(() => {
    // Calculate percentage difference from baseline (observationValueEnd)
    const currentValue = safeParseNumber(driverValueObject.value)
    const baseline = driverValueObject.observationValueEnd || 1
    const percentageDiff = calculatePercentageDiff(currentValue, baseline)

    // Display value with 2 decimal places (no snapping for display)
    const baseDisplayValue = Math.round(percentageDiff * 100) / 100

    // Convert actual value min/max to percentage min/max
    const minPercent = convertToPercentageLimit(min, baseline, -100)
    const maxPercent = convertToPercentageLimit(max, baseline, 100)

    return { baseDisplayValue, minPercent, maxPercent }
  }, [driverValueObject.value, driverValueObject.observationValueEnd, min, max])

  const applyValueChange = useCallback(
    (percentValue) => {
      const numericPercentValue = safeParseNumber(percentValue)

      // Apply smart snapping logic
      const targetPercentValue = applySmartSnapping(
        baseDisplayValue,
        numericPercentValue,
      )

      // Convert percentage back to actual value using current baseline
      const currentBaseline = driverValueObject.observationValueEnd || 1
      const actualValue = convertToAbsoluteValue(
        targetPercentValue,
        currentBaseline,
      )
      setScenarioDriverValue(scenarioId, driverId, actualValue)
    },
    [
      baseDisplayValue,
      driverValueObject.observationValueEnd,
      scenarioId,
      driverId,
      setScenarioDriverValue,
    ],
  )

  const { handleChange, handleBlur, displayValue } = useInputValueManager(
    baseDisplayValue,
    applyValueChange,
    minPercent, // Pass minPercent and maxPercent to the hook for clamping
    maxPercent,
  )

  return (
    <StyledInputNumber
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      min={minPercent}
      max={maxPercent}
      step={1}
      formatter={formatPercentage}
      size="sm"
      style={{ width: '5.5rem' }}
      {...props}
    />
  )
}

export default LadderPercentField
